import React, { useEffect, useState } from 'react';
import apiClient from '../api';
import ProfileEditForm from '../components/ProfileEditForm';
import PerformanceChart from '../components/PerformanceChart';
import { getPlayerPerformance } from '../api/stats';
import { useCurrentPlayer } from "../hooks/useCurrentPlayer";
import 'font-awesome/css/font-awesome.min.css';
import './MyAccount.css';

function MyAccount() {
    const [userData, setUserData] = useState(null);
    const [performance, setPerformance] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [editing, setEditing] = useState(false);

    const { player, loading: playerLoading, error: playerError } = useCurrentPlayer();

    const formatName = (str) => {
        if (!str) return '';
        const trimmed = str.trim();
        return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
    };

    const getInitials = (first, last) => {
        const f = first ? first[0] : '';
        const l = last ? last[0] : '';
        return (f + l).toUpperCase() || '?';
    };

    useEffect(() => {
        let alive = true;

        if (playerLoading) return;

        if (playerError) {
            setError('No se pudo obtener el usuario actual.');
            setLoading(false);
            return;
        }

        const playerId = player?.id ?? player?.player?.id;
        if (!playerId) {
            setLoading(false);
            return;
        }

        const fetchUser = async () => {
            setError(null);
            setLoading(true);
            try {
                const [{ data }, perfData] = await Promise.all([
                    apiClient.get(`/players/${playerId}/withStats`),
                    getPlayerPerformance(playerId).catch(() => []),
                ]);

                if (!alive) return;

                const playerInfo = data.player || {};
                const statsInfo = data.stats || {};

                setUserData({
                    ...playerInfo,
                    stats: statsInfo,
                    firstName: formatName(playerInfo.name),
                    lastName: formatName(playerInfo.lastName),
                    email: playerInfo.email,
                    teamName: playerInfo.teamName,
                    role: playerInfo.role
                });
                setPerformance(perfData || []);
            } catch (err) {
                console.error(err);
                if (err?.response?.status === 403 || err?.response?.status === 401) {
                    setError('Sesión expirada. Por favor, vuelve a iniciar sesión.');
                } else {
                    setError('Error al cargar los datos del perfil.');
                }
            } finally {
                if (alive) setLoading(false);
            }
        };

        fetchUser();
        return () => { alive = false; };
    }, [player, playerLoading, playerError]);

    if (loading || playerLoading) return (
        <div className="my-account-wrapper">
            <div className="loading-wrapper">
                <div className="spinner"></div>
                <p>Cargando perfil...</p>
            </div>
        </div>
    );

    if (error) return (
        <div className="my-account-wrapper">
            <div className="error-banner">
                <i className="fa fa-exclamation-triangle"></i> {error}
            </div>
        </div>
    );

    const heroTitle = userData?.firstName ? `Hola, ${userData.firstName}` : 'Mi Cuenta';

    return (
        <div className="my-account-wrapper">

            <div className="account-hero">
                <div style={{ width: '100%', maxWidth: '500px', alignSelf: 'center' }}>
                    
                </div>
                <div className="account-hero-content">
                    <span className="hero-badge">Perfil de Jugadora</span>
                    <h1 className="hero-title">{heroTitle}</h1>
                    <p className="hero-subtitle">Gestiona tu información personal y revisa tu rendimiento de la temporada.</p>
                </div>
            </div>

            <div className="account-grid">

                <div className="account-card profile-card">
                    <div className="card-header">
                        <div className="card-title">
                            <h2>Información Personal</h2>
                            <p>Tus datos de contacto y ficha.</p>
                        </div>
                        {!editing && (
                            <button className="btn-icon" onClick={() => setEditing(true)} title="Editar Perfil">
                                <i className="fa fa-pencil"></i>
                            </button>
                        )}
                    </div>

                    {editing ? (
                        <div className="edit-mode-wrapper">
                            <ProfileEditForm
                                player={userData}
                                onCancel={() => setEditing(false)}
                                onSuccess={() => window.location.reload()}
                            />
                        </div>
                    ) : (
                        <>
                            <div className="profile-main">
                                <div className="avatar-large">
                                    {getInitials(userData?.firstName, userData?.lastName)}
                                </div>
                                <div className="profile-identity">
                                    <h3>{userData?.firstName} {userData?.lastName}</h3>
                                    <span>{userData?.email}</span>
                                </div>
                            </div>

                            <div className="info-grid">
                                <div className="info-item">
                                    <span className="info-label">Equipo</span>
                                    <span className="info-value">{userData?.teamName || 'Sin asignar'}</span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Posición</span>
                                    <span className="info-value">{userData?.position || '-'}</span>
                                </div>
                            </div>
                        </>
                    )}
                </div>

                <div className="account-card stats-card">
                    <div className="card-header">
                        <div className="card-title">
                            <h2>Estadísticas</h2>
                            <p>Rendimiento en la temporada actual.</p>
                        </div>
                        <i className="fa fa-bar-chart" style={{color:'var(--brand-primary)', fontSize:'1.2rem'}}></i>
                    </div>

                    <div className="stats-grid">
                        <div className="stat-box">
                            <span className="stat-number">{userData?.stats?.gamesPlayed || 0}</span>
                            <span className="stat-name">Partidos</span>
                        </div>
                        <div className="stat-box">
                            <span className="stat-number">{userData?.stats?.points || 0}</span>
                            <span className="stat-name">Puntos</span>
                        </div>
                        <div className="stat-box">
                            <span className="stat-number">{userData?.stats?.wins || 0}</span>
                            <span className="stat-name">Victorias</span>
                        </div>
                        <div className="stat-box">
                            <span className="stat-number">{userData?.stats?.losses || 0}</span>
                            <span className="stat-name">Derrotas</span>
                        </div>
                        <div className="stat-box">
                            <span className="stat-number">{userData?.stats?.availability || 0}</span>
                            <span className="stat-name">Disponibilidad</span>
                        </div>
                    </div>
                </div>

                <div className="account-card">
                    <div className="card-header">
                        <div className="card-title">
                            <h2>Últimos partidos</h2>
                            <p>Resultados recientes.</p>
                        </div>
                        <i className="fa fa-history" style={{ color: 'var(--brand-primary)', fontSize: '1.2rem' }}></i>
                    </div>
                    {performance.length === 0 ? (
                        <p className="no-matches-msg">Sin partidos registrados todavía.</p>
                    ) : (
                        <div className="recent-matches-list">
                            {[...performance].reverse().slice(0, 5).map((p, i) => (
                                <div key={i} className={`recent-match-row ${p.won ? 'recent-match--win' : 'recent-match--loss'}`}>
                                    <span className={`recent-match-badge ${p.won ? 'badge--win' : 'badge--loss'}`}>
                                        {p.won ? 'V' : 'D'}
                                    </span>
                                    <span className="recent-match-opponent">vs {p.opponentName || '—'}</span>
                                    <span className="recent-match-date">
                                        {new Date(p.date).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="account-card">
                    <div className="card-header">
                        <div className="card-title">
                            <h2>Evolución</h2>
                            <p>Resultado acumulado partido a partido.</p>
                        </div>
                        <i className="fa fa-line-chart" style={{ color: 'var(--brand-primary)', fontSize: '1.2rem' }}></i>
                    </div>
                    <PerformanceChart data={performance} />
                </div>

            </div>
        </div>
    );
}

export default MyAccount;